<template lang="pug">
.stickers-block-editor
  .editor-header
    h3 {{ isCreating ? 'New Stickers' : 'Stickers' }}

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      el-form-item(label="Title" prop="title")
        el-input(
          v-model="formData.title"
          placeholder="Enter block title"
          size="default"
        )

      el-form-item(label="Columns Count" prop="columnsCount")
        el-select(
          v-model="formData.columnsCount"
          placeholder="Select columns count"
          size="default"
        )
          el-option(
            v-for="option in columnsOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          )

      el-form-item(label="Stickers" prop="stickersList")
        .reorder-actions
          .reorder-hint(v-if="formData.stickersList.length > 1")
            span Drag to reorder stickers
          el-button(
            type="primary"
            size="small"
            @click="showStickerSelector = true"
            plain
          ) Add Stickers
        .stickers-list

          .stickers-list-content(v-if="formData.stickersList.length > 0")
            draggable.selected-stickers-list(
              v-model="formData.stickersList"
              handle=".drag-handle"
              ghost-class="ghost-sticker"
              item-key="id"
              @start="dragStart"
              @end="dragEnd"
            )
              template(#item="{ element: stickerId, index }")
                .selected-sticker-item(:key="stickerId")
                  .selected-sticker-content
                    .drag-handle.cursor-move
                      fai(type="fas" icon="grip-vertical").size-14
                    .selected-sticker-thumbnail
                      img(
                        v-if="getStickerAvatar(stickerId)"
                        :src="getStickerAvatar(stickerId)"
                      )
                      .no-image(v-else)
                    .selected-sticker-info
                      .selected-sticker-title
                        | {{ getStickerName(stickerId) }}
                      .selected-sticker-creator(v-if="getStickerCreator(stickerId)")
                        | {{ getStickerCreator(stickerId) }}
                  .selected-sticker-actions
                    el-button(
                      type="danger"
                      size="small"
                      text
                      @click="removeSticker(index)"
                    ).has-fai
                      fai(type="fal" icon="trash").size-14

          .stickers-list-empty(v-else)
            span No stickers selected. Click "Add Stickers" to select stickers.

      .editor-actions
        el-button(
          @click="handleCancel"
          size="default"
        ) Cancel
        el-button(
          type="primary"
          @click="handleSave"
          size="default"
          :loading="saving"
        ) Save

  // Sticker selector dialog
  DialogSelectStickers(
    v-model="showStickerSelector"
    :preSelectedStickers="formData.stickersList"
    @confirm="handleStickersSelected"
  )
</template>

<script>
import {
  defineComponent, ref, reactive, computed,
} from 'vue';
import { ElMessage } from 'element-plus';
import draggable from 'vuedraggable';
import useStaticPageUtils from '../shared/index.ts';
import DialogSelectStickers from './DialogSelectStickers.vue';

export default defineComponent({
  name: 'StickersBlock',
  components: { DialogSelectStickers, draggable },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateStickersBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const saving = ref(false);
    const showStickerSelector = ref(false);

    // Store stickers selected from dialog that might not be in staticPageData yet
    const dialogSelectedStickers = ref([]);

    // Form data
    const formData = reactive({
      title: props.block?.details?.title || '',
      columnsCount: props.block?.details?.columnsCount || 2,
      stickersList: props.block?.details?.stickersList || [],
    });

    // Columns options
    const columnsOptions = [
      { value: 1, label: '1 Column' },
      { value: 2, label: '2 Columns' },
      { value: 3, label: '3 Columns' },
    ];

    // Form validation rules
    const rules = {
      title: [
        { required: true, message: 'Please enter block title', trigger: 'blur' },
      ],
      columnsCount: [
        { required: true, message: 'Please select columns count', trigger: 'change' },
      ],
      stickersList: [
        {
          type: 'array',
          required: true,
          message: 'Please select at least one sticker',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (value.length === 0) {
              callback(new Error('Please select at least one sticker'));
            } else {
              callback();
            }
          },
        },
      ],
    };

    // Combine stickers from static page data and dialog selections
    const stickers = computed(() => {
      const staticPageStickers = props.staticPageData?.stickers || [];
      const allStickers = [...staticPageStickers];

      // Add stickers from dialog that aren't already in staticPageData
      dialogSelectedStickers.value.forEach((dialogSticker) => {
        if (!allStickers.find((s) => s.id === dialogSticker.id)) {
          allStickers.push(dialogSticker);
        }
      });

      return allStickers;
    });

    // Get sticker name by ID
    function getStickerName(stickerId) {
      const sticker = stickers.value.find((s) => s.id === stickerId);
      return sticker ? sticker.title : '';
    }

    // Get sticker avatar by ID
    function getStickerAvatar(stickerId) {
      const sticker = stickers.value.find((s) => s.id === stickerId);
      return sticker ? sticker.url : null;
    }

    // Get sticker creator by ID
    function getStickerCreator(stickerId) {
      const sticker = stickers.value.find((s) => s.id === stickerId);
      if (!sticker) return '';
      if (sticker.organization) return sticker.organization.name;
      if (sticker.user) return `${sticker.user.firstname} ${sticker.user.lastname}`;
      return '';
    }

    // Handle stickers selected from dialog
    function handleStickersSelected(selectedIds, selectedStickerObjects = []) {
      formData.stickersList = selectedIds;
      // Store the full sticker objects for display
      dialogSelectedStickers.value = selectedStickerObjects;
    }

    // Remove sticker from list
    function removeSticker(index) {
      const removedStickerId = formData.stickersList[index];
      formData.stickersList.splice(index, 1);

      // Also remove from dialog selected stickers if it exists there
      const dialogIndex = dialogSelectedStickers.value.findIndex((s) => s.id === removedStickerId);
      if (dialogIndex !== -1) {
        dialogSelectedStickers.value.splice(dialogIndex, 1);
      }
    }

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;

        const input = {
          title: formData.title,
          columnsCount: formData.columnsCount,
          stickersList: formData.stickersList,
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
        };

        const result = await createOrUpdateStickersBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'Stickers block created successfully' : 'Stickers block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving stickers block:', error);
        ElMessage.error('Failed to save stickers block');
      } finally {
        saving.value = false;
      }
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    // Drag event handlers
    function dragStart() {
      // Optional: Add any logic needed when dragging starts
    }

    function dragEnd() {
      // Optional: Add any logic needed when dragging ends
    }

    return {
      formRef,
      formData,
      rules,
      saving,
      columnsOptions,
      stickers,
      dialogSelectedStickers,
      getStickerName,
      getStickerAvatar,
      getStickerCreator,
      showStickerSelector,
      handleStickersSelected,
      removeSticker,
      handleSave,
      handleCancel,
      dragStart,
      dragEnd,
    };
  },
});
</script>

<style scoped>
.stickers-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 500px;
  margin-top: 20px;
}

.reorder-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.stickers-list {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
}

.reorder-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
}

.stickers-list-content {
  max-height: 300px;
  overflow-y: auto;
}

.selected-stickers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-sticker-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.selected-sticker-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.drag-handle {
  cursor: move; /* fallback */
  cursor: grab;
  color: #909399;
  margin-right: 4px;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  color: #1EBEAF;
}

.drag-handle:active {
  cursor: grabbing;
}

.ghost-sticker {
  opacity: 0.5;
  background: #e6f7f6;
  border: 1px dashed #1EBEAF;
}

.selected-sticker-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.selected-sticker-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e0e0e0;
  color: #606266;
  font-size: 12px;
  border: 1px solid #d4d4d4;
}

.selected-sticker-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.selected-sticker-title {
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-sticker-creator {
  font-size: 0.85em;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-sticker-actions {
  display: flex;
  align-items: center;
}

.stickers-list-empty {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
